import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { HttpRequest } from "../base/HttpModels";
import { IChangeOrderStatusRequest } from "./ordersTypes";
import { ORDER_CHANGE_STATUS } from "./orderHistoryActions";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { changeOrderStatus } from "./ordersSlice";
import { CAMERIERE, intFromRole } from "../utils/roles";

export interface OrderHistoryItemProps {
  id: number;
  date: Date;
  table: string;
  takenBy?: string;
  paidBy?: string;
  servedBy?: string;
  closedBy?: string;
  openedById?: number;
  orderStatusId?: number;
  price: number;
  isTakeAway?: boolean;
}

const OrderHistoryItem = (props: OrderHistoryItemProps) => {
  const {
    id,
    date,
    table,
    takenBy,
    paidBy,
    servedBy,
    closedBy,
    openedById,
    orderStatusId = 1,
    price,
    isTakeAway,
  } = props;
  const token = useAppSelector((state) => state.loginState.token);
  const role = useAppSelector((state) => state.loginState.role);
  const userName = useAppSelector((state) => state.loginState.name);
  const users = useAppSelector((state) => state.userState.users);
  const dispatch = useAppDispatch();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const currentUser = users.find((u) => u.name === userName);
  const currentUserId = currentUser?.id;

  // Can delete: Admin (1) & Cassiere (2) always; Cameriere (3) only if order was opened by them and is unpaid (status 1)
  const canDelete =
    orderStatusId !== 5 &&
    (role === 1 ||
      role === 2 ||
      (role === 3 &&
        orderStatusId === 1 &&
        currentUserId !== undefined &&
        openedById === currentUserId));

  let backgroundColor = "#ff000022";

  if (orderStatusId === 5) {
    backgroundColor = "#9e9e9e33";
  } else if (paidBy) {
    backgroundColor = "#fcba0322";
  }

  if (servedBy && orderStatusId !== 5) {
    backgroundColor = "#ecfc0322";
  }

  if (closedBy && orderStatusId !== 5) {
    backgroundColor = "#73fc0322";
  }

  return (
    <div
      style={{
        paddingTop: "8px",
      }}
    >
      <Card variant="elevation" sx={{ width: "100%" }}>
        <CardContent
          sx={{
            padding: "8px",
            backgroundColor: backgroundColor,
          }}
        >
          <Grid container>
            <Grid item xs={6} alignItems="center">
              <Typography variant="h6" align="left">
                Ordine: {id}
              </Typography>
            </Grid>
            <Grid item xs={6} alignItems="center">
              <Typography variant="h6" align="right">
                {date.getDate().toString().padStart(2, "0") +
                  "/" +
                  (date.getMonth() + 1).toString().padStart(2, "0") +
                  "/" +
                  date.getFullYear() +
                  " " +
                  date.getHours().toString().padStart(2, "0") +
                  ":" +
                  date.getMinutes().toString().padStart(2, "0")}
              </Typography>
            </Grid>
            <Grid item xs={6} alignItems="center">
              <Typography variant="h6" align="left">
                Tavolo: {table}
              </Typography>
            </Grid>
            <Grid item xs={6} alignItems="center">
              <Typography variant="h6" align="right">
                Totale: € {price}
              </Typography>
            </Grid>
            <Grid item xs={12} alignItems="center">
              <Typography variant="body1" align="left">
                Ordine preso da: <b>{takenBy}</b>
              </Typography>
            </Grid>
            <Grid item xs={12} alignItems="center">
              {paidBy ? (
                <Typography variant="body1" align="left">
                  Segnato pagato da: <b>{paidBy}</b>
                </Typography>
              ) : (
                <Box textAlign="center">
                  <Button
                    variant="outlined"
                    disabled={token === undefined || role === 3} // Only cashier+ can mark orders as paid
                    onClick={(event) => {
                      if (token === undefined) {
                        return;
                      }
                      const request: HttpRequest<IChangeOrderStatusRequest> = {
                        action: ORDER_CHANGE_STATUS,
                        parameters: {
                          order_id: id,
                          order_status_id: 2, // PAID
                          user_token: token,
                        },
                      };
                      dispatch(changeOrderStatus(request));
                    }}
                  >
                    Segna come pagato
                  </Button>
                </Box>
              )}
            </Grid>
            {paidBy ? (
              <Grid item xs={12} alignItems="center" sx={{ marginTop: "8px" }}>
                {servedBy ? (
                  <Typography variant="body1" align="left">
                    Preso in carico da: <b>{servedBy}</b>
                  </Typography>
                ) : role !== intFromRole(CAMERIERE) ? (
                  <Box textAlign="center">
                    <Button
                      variant="outlined"
                      onClick={(event) => {
                        if (token === undefined) {
                          return;
                        }
                        const request: HttpRequest<IChangeOrderStatusRequest> =
                          {
                            action: ORDER_CHANGE_STATUS,
                            parameters: {
                              order_id: id,
                              order_status_id: 3, // SERVED
                              user_token: token,
                            },
                          };
                        dispatch(changeOrderStatus(request)).then((result) => {
                          if (result.payload !== null) {
                            const printRoute = isTakeAway 
                              ? "/#/order_detail_takeaway/" + id
                              : "/#/order_detail/" + id;
                            window.open(
                              window.location.origin + printRoute,
                              "_blank",
                              "width=400"
                            );
                          }
                        });
                      }}
                    >
                      Stampa
                    </Button>
                  </Box>
                ) : null}
              </Grid>
            ) : null}
            {servedBy ? (
              <Grid item xs={12} alignItems="center" sx={{ marginTop: "8px" }}>
                {closedBy ? (
                  <Typography variant="body1" align="left">
                    Completato da: <b>{closedBy}</b>
                  </Typography>
                ) : (
                  <Box textAlign="center">
                    <Button
                      variant="outlined"
                      onClick={(event) => {
                        if (token === undefined) {
                          return;
                        }
                        const request: HttpRequest<IChangeOrderStatusRequest> =
                          {
                            action: ORDER_CHANGE_STATUS,
                            parameters: {
                              order_id: id,
                              order_status_id: 4, // COMPLETED
                              user_token: token,
                            },
                          };
                        dispatch(changeOrderStatus(request));
                      }}
                    >
                      Segna come completato
                    </Button>
                  </Box>
                )}
              </Grid>
            ) : null}
          </Grid>
          {servedBy && !closedBy && role !== intFromRole(CAMERIERE) ? (
            <Grid item xs={12} alignItems="center" sx={{ marginTop: "8px" }}>
              <Box textAlign="center">
                <Button
                  variant="outlined"
                  onClick={(event) => {
                    const printRoute = isTakeAway 
                      ? "/#/order_detail_takeaway/" + id
                      : "/#/order_detail/" + id;
                    window.open(
                      window.location.origin + printRoute,
                      "_blank",
                      "width=400"
                    );
                  }}
                >
                  Ristampa
                </Button>
              </Box>
            </Grid>
          ) : null}
          <Grid item xs={12} alignItems="center" sx={{ marginTop: "8px" }}>
            <Box textAlign="center" sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
              <Button
                variant="outlined"
                onClick={(event) => {
                  window.open(
                    window.location.origin + "/#/order_preview/" + id,
                    "_blank"
                  );
                }}
              >
                Visualizza
              </Button>
              {canDelete && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  Cancella ordine
                </Button>
              )}
            </Box>
          </Grid>
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Conferma Eliminazione Ordine</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler cancellare l'ordine #{id} (Tavolo: {table})?
            Questa operazione non può essere annullata.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annulla</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setDeleteDialogOpen(false);
              if (token === undefined) {
                return;
              }
              const request: HttpRequest<IChangeOrderStatusRequest> = {
                action: ORDER_CHANGE_STATUS,
                parameters: {
                  order_id: id,
                  order_status_id: 5, // DELETED
                  user_token: token,
                },
              };
              dispatch(changeOrderStatus(request));
            }}
          >
            Conferma Eliminazione
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OrderHistoryItem;
